import FlipIcon from '@mui/icons-material/Flip';
import HelpIcon from '@mui/icons-material/Help';
import OverflowMenuIcon from '@mui/icons-material/MoreVert';
import SignOutIcon from '@mui/icons-material/PowerSettingsNew';
import {Alert} from '@mui/lab';
import {Box, CircularProgress, useTheme} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography/Typography';
import type * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import GlenbrookLogo from '../assets/glenbrook225.png';
import {authManager} from '../components/Auth/AuthManager';
import BarcodeIcon from '../components/Icons/BarcodeIcon';
import PaddedContainer from '../components/PaddedContainer';
import type {IdCard} from '../utils/IdCard';
import {clearIdCard, getIdCard, isIdCardExpired} from '../utils/IdCard';
import DownloadIdController from './DownloadId/DownloadIdController';
import HelpDialog from './Help/HelpDialog';
import SubmitPhotoController from './SubmitPhoto/SubmitPhotoController';
import type {View} from './ViewId/ViewId';
import ViewId from './ViewId/ViewId';

const Main: React.FC = () => {
    const theme = useTheme();
    const [idCard, setIdCard] = useState<IdCard | null | undefined>(undefined);
    const [view, setView] = useState<View>('front');
    const [overflowMenuAnchor, setOverflowMenuAnchor] = useState<HTMLElement | null>(null);
    const [submitPhotoDialogOpen, setSubmitPhotoDialogOpen] = useState(false);
    const [downloadIdDialogOpen, setDownloadIdDialogOpen] = useState(false);
    const [helpDialogOpen, setHelpDialogOpen] = useState(false);
    const [authenticated, setAuthenticated] = useState(false);
    const [idCardExpired, setIdCardExpired] = useState(false);

    useEffect(() => {
        void (async () => {
            let idCard: IdCard | null;

            try {
                idCard = await getIdCard();
                setIdCardExpired(isIdCardExpired(idCard));
            } catch {
                idCard = null;
            }

            setIdCard(idCard);
        })();
    }, []);

    useEffect(() => {
        const handleAuth = () => {
            setAuthenticated(true);
        };

        const handleExpire = () => {
            setAuthenticated(false);
        };

        authManager.addEventListener('auth', handleAuth);
        authManager.addEventListener('expire', handleExpire);

        return () => {
            authManager.removeEventListener('auth', handleAuth);
            authManager.removeEventListener('expire', handleExpire);
        };
    }, []);

    useEffect(() => {
        if (!idCard || idCardExpired) {
            return;
        }

        const interval = window.setInterval(() => {
            if (isIdCardExpired(idCard)) {
                setIdCardExpired(true);
            }
        }, 1000 * 60);

        return () => {
            window.clearInterval(interval);
        };
    }, [idCard, idCardExpired]);

    const handleDownloadIdDialogClose = useCallback(() => {
        setDownloadIdDialogOpen(false);
    }, []);

    const handleDownloadSuccess = useCallback((idCard: IdCard) => {
        setView('front');
        setIdCard(idCard);
        setIdCardExpired(false);
    }, []);

    const handleSubmitPhotoDialogClose = useCallback(() => {
        setSubmitPhotoDialogOpen(false);
    }, []);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <AppBar position='static'>
                <Toolbar>
                    <Typography
                        variant='h6'
                        sx={{
                            flexGrow: 1,
                        }}
                    >
                        Your Digital ID
                    </Typography>

                    {idCard ? (
                        <>
                            {!idCardExpired && (
                                <>
                                    <IconButton
                                        color='inherit'
                                        onClick={() => {
                                            setView(view === 'front' ? 'back' : 'front');
                                        }}
                                    >
                                        <FlipIcon />
                                    </IconButton>
                                    <IconButton
                                        color='inherit'
                                        onClick={() => {
                                            setView('barcode');
                                        }}
                                    >
                                        <BarcodeIcon />
                                    </IconButton>
                                </>
                            )}

                            <IconButton
                                color='inherit'
                                edge='end'
                                onClick={event => {
                                    setOverflowMenuAnchor(event.currentTarget);
                                }}
                            >
                                <OverflowMenuIcon />
                            </IconButton>

                            <Menu
                                anchorEl={overflowMenuAnchor}
                                open={Boolean(overflowMenuAnchor)}
                                keepMounted
                                onClose={() => {
                                    setOverflowMenuAnchor(null);
                                }}
                                // getContentAnchorEl={null}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <MenuItem
                                    onClick={() => {
                                        setOverflowMenuAnchor(null);
                                        setDownloadIdDialogOpen(true);
                                    }}
                                >
                                    Refresh ID
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        setOverflowMenuAnchor(null);
                                        setSubmitPhotoDialogOpen(true);
                                    }}
                                >
                                    Submit new photo for review
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        setOverflowMenuAnchor(null);
                                        setHelpDialogOpen(true);
                                    }}
                                >
                                    Show help
                                </MenuItem>
                                <Divider />
                                <MenuItem
                                    onClick={() => {
                                        setOverflowMenuAnchor(null);
                                        clearIdCard();
                                        setIdCard(null);
                                        authManager.clearUser();
                                    }}
                                >
                                    Sign out
                                </MenuItem>
                            </Menu>
                        </>
                    ) : (
                        <>
                            {authenticated && (
                                <IconButton
                                    color='inherit'
                                    onClick={() => {
                                        authManager.clearUser();
                                    }}
                                >
                                    <SignOutIcon />
                                </IconButton>
                            )}

                            <IconButton
                                color='inherit'
                                edge='end'
                                onClick={() => {
                                    setHelpDialogOpen(true);
                                }}
                            >
                                <HelpIcon />
                            </IconButton>
                        </>
                    )}
                </Toolbar>
            </AppBar>

            {idCard === undefined ? (
                <Box
                    sx={{
                        flexGrow: 1,
                        overflow: 'hidden',
                        // For Safari, we have to force a specific height.
                        height: 'calc(100vh - 64px)',
                        [`${theme.breakpoints.up('xs')} and (orientation: landscape)`]: {
                            height: 'calc(100vh - 48px)',
                        },
                        [theme.breakpoints.up('sm')]: {
                            height: 'calc(100vh - 64px)',
                        },
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : idCard === null ? (
                <PaddedContainer>
                    <Box
                        sx={{
                            textAlign: 'center',
                        }}
                    >
                        <img
                            src={GlenbrookLogo}
                            width={200}
                            height={134.3}
                            alt='Glenbrook High Schools - District 225'
                        />
                    </Box>
                    <Divider />
                    <Box
                        sx={{
                            margin: theme.spacing(2, 0),
                        }}
                    >
                        <Grid container spacing={2} direction='column' alignItems='center'>
                            <Grid item>
                                <Button
                                    variant='contained'
                                    color='secondary'
                                    onClick={() => {
                                        setDownloadIdDialogOpen(true);
                                    }}
                                >
                                    Download your ID
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant='contained'
                                    color='secondary'
                                    onClick={() => {
                                        setSubmitPhotoDialogOpen(true);
                                    }}
                                >
                                    Submit new photo for review
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                    <Divider />
                    <Box
                        sx={{
                            marginTop: theme.spacing(2),
                        }}
                    >
                        <Typography paragraph>
                            Utilizing this app, students and staff can access their Glenbrook
                            Digital ID Card, or submit a new ID card photo. Submitted photos will be
                            reviewed by the Dean's Office, and digital IDs cannot be downloaded
                            until an approved photo is on file.
                        </Typography>
                        <Typography paragraph>
                            If you need assistance with the Digital ID card system, please contact
                            the Help Desk at{' '}
                            <Link href='tel:+1-847-486-4555'>(847)&nbsp;486-4555</Link>.
                        </Typography>
                    </Box>
                </PaddedContainer>
            ) : idCardExpired ? (
                <PaddedContainer>
                    <Alert
                        severity='error'
                        action={
                            <Button
                                color='inherit'
                                size='small'
                                onClick={() => {
                                    setDownloadIdDialogOpen(true);
                                }}
                            >
                                Refresh ID
                            </Button>
                        }
                    >
                        Your Digital ID card has expired.
                    </Alert>
                </PaddedContainer>
            ) : (
                <Box
                    sx={{
                        flexGrow: 1,
                        overflow: 'hidden',

                        // For Safari, we have to force a specific height.
                        height: 'calc(100vh - 64px)',
                        [`${theme.breakpoints.up('xs')} and (orientation: landscape)`]: {
                            height: 'calc(100vh - 64px)',
                        },
                        [theme.breakpoints.up('sm')]: {
                            height: 'calc(100vh - 64px)',
                        },
                    }}
                >
                    <ViewId idCard={idCard} view={view} />
                </Box>
            )}

            <DownloadIdController
                open={downloadIdDialogOpen}
                onClose={handleDownloadIdDialogClose}
                onDownloadSuccess={handleDownloadSuccess}
            />
            <SubmitPhotoController
                open={submitPhotoDialogOpen}
                onClose={handleSubmitPhotoDialogClose}
            />

            <HelpDialog
                open={helpDialogOpen}
                onClose={() => {
                    setHelpDialogOpen(false);
                }}
            />
        </Box>
    );
};

export default Main;
